import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Image, Video, FileText, FolderOpen, Upload, Plus, Trash2, ArrowLeft, Download, Loader2, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function formatSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function MediaPanel({ orgId, teams, userRole }) {
  const { isDark } = useTheme();
  const [folders, setFolders] = useState([]);
  const [activeFolder, setActiveFolder] = useState(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('folders');
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [folderForm, setFolderForm] = useState({ name: '', description: '' });
  const [caption, setCaption] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [imagePreviews, setImagePreviews] = useState({});
  const fileInputRef = useRef(null);
  const dropRef = useRef(null);

  useEffect(() => { if (orgId) fetchFolders(); }, [orgId]);

  const fetchFolders = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/media/${orgId}/folders`, { withCredentials: true });
      setFolders(res.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const openFolder = async (folder) => {
    setActiveFolder(folder);
    setView('files');
    try {
      const res = await axios.get(`${API_URL}/api/media/${orgId}/folders/${folder.id}/files`, { withCredentials: true });
      setFiles(res.data || []);
    } catch (err) { console.error(err); setFiles([]); }
  };

  const createFolder = async () => {
    try {
      await axios.post(`${API_URL}/api/media/${orgId}/folders`, folderForm, { withCredentials: true });
      setShowNewFolder(false);
      setFolderForm({ name: '', description: '' });
      fetchFolders();
    } catch (err) { console.error(err); }
  };

  const deleteFolder = async (folderId) => {
    if (!window.confirm('Delete this folder and all its files?')) return;
    try {
      await axios.delete(`${API_URL}/api/media/${orgId}/folders/${folderId}`, { withCredentials: true });
      fetchFolders();
      if (activeFolder?.id === folderId) { setView('folders'); setActiveFolder(null); }
    } catch (err) { console.error(err); }
  };

  const handleFileSelect = (e) => {
    const newFiles = Array.from(e.target.files);
    setSelectedFiles(prev => [...prev, ...newFiles]);
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const droppedFiles = Array.from(e.dataTransfer.files);
    setSelectedFiles(prev => [...prev, ...droppedFiles]);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const removeSelectedFile = (idx) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const uploadFiles = async () => {
    if (!selectedFiles.length || !activeFolder) return;
    setUploading(true);
    let success = 0;
    for (let i = 0; i < selectedFiles.length; i++) {
      setUploadProgress(`Uploading ${i + 1} of ${selectedFiles.length}...`);
      const formData = new FormData();
      formData.append('file', selectedFiles[i]);
      formData.append('caption', caption);
      try {
        await axios.post(
          `${API_URL}/api/media/${orgId}/folders/${activeFolder.id}/upload`,
          formData,
          { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
        );
        success++;
      } catch (err) { console.error(`Failed: ${selectedFiles[i].name}`, err); }
    }
    setUploading(false);
    setUploadProgress('');
    setShowUpload(false);
    setSelectedFiles([]);
    setCaption('');
    openFolder(activeFolder);
  };

  // Fetch image blob for preview
  const loadPreview = async (fileRecord) => {
    if (imagePreviews[fileRecord.id]) return;
    if (!fileRecord.content_type?.startsWith('image/')) return;
    try {
      const res = await axios.get(`${API_URL}/api/media/${orgId}/files/${fileRecord.id}/download`, {
        withCredentials: true, responseType: 'blob'
      });
      const blobUrl = URL.createObjectURL(res.data);
      setImagePreviews(prev => ({ ...prev, [fileRecord.id]: blobUrl }));
    } catch (err) { /* no preview */ }
  };

  useEffect(() => {
    files.forEach(f => { if (f.content_type?.startsWith('image/')) loadPreview(f); });
    return () => { Object.values(imagePreviews).forEach(url => URL.revokeObjectURL(url)); };
  }, [files]);

  const downloadFile = async (file) => {
    try {
      const res = await axios.get(`${API_URL}/api/media/${orgId}/files/${file.id}/download`, {
        withCredentials: true, responseType: 'blob'
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.original_filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) { console.error(err); }
  };

  const deleteFile = async (fileId) => {
    try {
      await axios.delete(`${API_URL}/api/media/${orgId}/files/${fileId}`, { withCredentials: true });
      openFolder(activeFolder);
    } catch (err) { console.error(err); }
  };

  const typeIcon = (contentType) => {
    if (!contentType) return <FileText className="h-5 w-5 text-green-500" />;
    if (contentType.startsWith('image/')) return <Image className="h-5 w-5 text-blue-500" />;
    if (contentType.startsWith('video/')) return <Video className="h-5 w-5 text-purple-500" />;
    return <FileText className="h-5 w-5 text-green-500" />;
  };

  const goBack = () => { setView('folders'); setActiveFolder(null); fetchFolders(); };

  return (
    <div className="space-y-4" data-testid="media-panel">
      <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              {view === 'files' && activeFolder ? (
                <>
                  <Button size="sm" variant="ghost" onClick={goBack} data-testid="media-back-btn"><ArrowLeft className="h-4 w-4" /></Button>
                  <FolderOpen className="h-5 w-5 text-amber-500" />
                  {activeFolder.name}
                  <Badge variant="outline" className="text-xs">{files.length} files</Badge>
                </>
              ) : (
                <>
                  <FolderOpen className="h-5 w-5 text-amber-500" /> Media Library
                  <Badge variant="outline" className="text-xs">{folders.length} folders</Badge>
                </>
              )}
            </CardTitle>
            <div className="flex gap-2">
              {view === 'folders' && (
                <Button size="sm" className="bg-amber-600 text-white hover:bg-amber-700"
                  onClick={() => setShowNewFolder(true)} data-testid="media-new-folder-btn">
                  <Plus className="h-4 w-4 mr-1" /> New Folder
                </Button>
              )}
              {view === 'files' && (
                <Button size="sm" className="bg-amber-600 text-white hover:bg-amber-700"
                  onClick={() => setShowUpload(true)} data-testid="media-upload-btn">
                  <Upload className="h-4 w-4 mr-1" /> Upload Files
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* FOLDER GRID */}
          {view === 'folders' && (
            loading ? <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>Loading...</p> :
            folders.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {folders.map(folder => (
                  <div key={folder.id}
                    className={`p-4 rounded-lg border cursor-pointer transition-colors text-center relative group ${isDark ? 'border-slate-700 hover:bg-slate-700/50' : 'border-slate-200 hover:bg-slate-50'}`}
                    data-testid={`folder-${folder.id}`}>
                    <div onClick={() => openFolder(folder)}>
                      <FolderOpen className="h-8 w-8 mx-auto text-amber-500" />
                      <p className={`font-medium mt-2 text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}>{folder.name}</p>
                      <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{folder.file_count || 0} items</p>
                      {folder.description && <p className={`text-xs mt-1 truncate ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>{folder.description}</p>}
                    </div>
                    <button onClick={(e) => { e.stopPropagation(); deleteFolder(folder.id); }}
                      className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-500 transition-opacity"
                      data-testid={`delete-folder-${folder.id}`}>
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className={`text-center py-12 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                <FolderOpen className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p className="font-medium">No Media Folders</p>
                <p className="text-sm mt-1">Create folders to organize photos, videos, and files</p>
              </div>
            )
          )}

          {/* FILES VIEW */}
          {view === 'files' && (
            files.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {files.map(file => (
                  <div key={file.id} className={`p-3 rounded-lg border relative group ${isDark ? 'border-slate-700' : 'border-slate-200'}`}
                    data-testid={`file-${file.id}`}>
                    <div className={`h-28 rounded flex items-center justify-center mb-2 overflow-hidden ${isDark ? 'bg-slate-800' : 'bg-slate-100'}`}>
                      {imagePreviews[file.id] ? (
                        <img src={imagePreviews[file.id]} alt={file.original_filename} className="h-full w-full object-cover rounded" />
                      ) : typeIcon(file.content_type)}
                    </div>
                    <p className={`text-xs truncate font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{file.original_filename}</p>
                    <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                      {formatSize(file.size)} {file.uploaded_by_name && `\u00B7 ${file.uploaded_by_name}`}
                    </p>
                    {file.caption && <p className={`text-xs mt-0.5 truncate ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>{file.caption}</p>}
                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 flex gap-1 transition-opacity">
                      <button onClick={() => downloadFile(file)} className="text-blue-400 hover:text-blue-500" data-testid={`download-file-${file.id}`}>
                        <Download className="h-3.5 w-3.5" />
                      </button>
                      <button onClick={() => deleteFile(file.id)} className="text-red-400 hover:text-red-500" data-testid={`delete-file-${file.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className={`text-center py-8 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                <Upload className="h-10 w-10 mx-auto mb-2 opacity-30" />
                <p className="text-sm">Folder is empty — upload files to get started</p>
              </div>
            )
          )}
        </CardContent>
      </Card>

      {/* NEW FOLDER DIALOG */}
      <Dialog open={showNewFolder} onOpenChange={setShowNewFolder}>
        <DialogContent className={isDark ? 'bg-slate-800 border-slate-700 text-white' : ''}>
          <DialogHeader><DialogTitle>New Folder</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className={isDark ? 'text-slate-300' : ''}>Name</Label>
              <Input value={folderForm.name} onChange={e => setFolderForm({...folderForm, name: e.target.value})}
                placeholder="e.g., Game Day Photos" className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''} data-testid="folder-name" />
            </div>
            <div>
              <Label className={isDark ? 'text-slate-300' : ''}>Description</Label>
              <Input value={folderForm.description} onChange={e => setFolderForm({...folderForm, description: e.target.value})}
                placeholder="Optional" className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''} />
            </div>
            <Button onClick={createFolder} disabled={!folderForm.name.trim()} className="w-full bg-amber-600 text-white hover:bg-amber-700" data-testid="create-folder-btn">
              Create Folder
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* UPLOAD DIALOG */}
      <Dialog open={showUpload} onOpenChange={(open) => { if (!uploading) { setShowUpload(open); if (!open) { setSelectedFiles([]); setCaption(''); } } }}>
        <DialogContent className={isDark ? 'bg-slate-800 border-slate-700 text-white' : ''}>
          <DialogHeader><DialogTitle>Upload Files</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {/* Drop Zone */}
            <div ref={dropRef}
              onDrop={handleDrop} onDragOver={handleDragOver}
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                isDark ? 'border-slate-600 hover:border-amber-500 text-slate-400' : 'border-slate-300 hover:border-amber-500 text-slate-500'
              }`} data-testid="upload-dropzone">
              <Upload className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm font-medium">Drop files here or click to browse</p>
              <p className="text-xs mt-1">Max 25MB per file</p>
            </div>
            <input ref={fileInputRef} type="file" multiple className="hidden" onChange={handleFileSelect} data-testid="file-input" />

            {/* Selected Files List */}
            {selectedFiles.length > 0 && (
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {selectedFiles.map((f, i) => (
                  <div key={i} className={`flex items-center justify-between px-2 py-1 rounded text-xs ${isDark ? 'bg-slate-700' : 'bg-slate-100'}`}>
                    <span className="truncate flex-1">{f.name} ({formatSize(f.size)})</span>
                    <button onClick={() => removeSelectedFile(i)} className="ml-2 text-red-400 hover:text-red-500"><X className="h-3 w-3" /></button>
                  </div>
                ))}
              </div>
            )}

            <div>
              <Label className={isDark ? 'text-slate-300' : ''}>Caption (optional)</Label>
              <Input value={caption} onChange={e => setCaption(e.target.value)}
                placeholder="Add a caption for these files" className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''} data-testid="upload-caption" />
            </div>

            <Button onClick={uploadFiles} disabled={!selectedFiles.length || uploading}
              className="w-full bg-amber-600 text-white hover:bg-amber-700" data-testid="upload-submit-btn">
              {uploading ? (
                <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> {uploadProgress}</>
              ) : (
                <><Upload className="h-4 w-4 mr-1" /> Upload {selectedFiles.length} file{selectedFiles.length !== 1 ? 's' : ''}</>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
